import { createRootRoute, createRoute, createRouter } from '@tanstack/react-router';
import { App } from './App.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { ImportsPage } from './pages/ImportsPage.js';
import { EhrsPage } from './pages/EhrsPage.js';
import { EhrDetailPage } from './pages/EhrDetailPage.js';
import { RecipesPage } from './pages/RecipesPage.js';
import { BuildsPage } from './pages/BuildsPage.js';
import { BuildDetailPage } from './pages/BuildDetailPage.js';
import { ConflictsPage } from './pages/ConflictsPage.js';
import { SettingsPage } from './pages/SettingsPage.js';
import { KnowledgePage } from './pages/KnowledgePage.js';
import { UserDetailPage } from './pages/UserDetailPage.js';
import { AuditLogsPage } from './pages/AuditLogsPage.js';
import { SessionDetailPage } from './pages/SessionDetailPage.js';
import { CacheBrowserPage } from './pages/CacheBrowserPage.js';
import { TicketsPage } from './pages/TicketsPage.js';
import { TicketDetailPage } from './pages/TicketDetailPage.js';
import { CalendarPage } from './pages/CalendarPage.js';
import { ReviewQueuePage } from './pages/ReviewQueuePage.js';
import { QuarantinePage } from './pages/QuarantinePage.js';
import { IdMapPage } from './pages/IdMapPage.js';
import { StoragePage } from './pages/StoragePage.js';
import { OperationsPage } from './pages/OperationsPage.js';
import { SharesPage } from './pages/SharesPage.js';
import { ShareDetailPage } from './pages/ShareDetailPage.js';

const rootRoute = createRootRoute({ component: App });

const dashboardRoute = createRoute({ getParentRoute: () => rootRoute, path: '/', component: DashboardPage });
const importsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/imports', component: ImportsPage });
const ehrsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/ehrs', component: EhrsPage });
const ehrDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ehrs/$id',
  component: function EhrDetailWrapper() {
    const { id } = ehrDetailRoute.useParams();
    return <EhrDetailPage ehrId={id} />;
  },
});
const recipesRoute = createRoute({ getParentRoute: () => rootRoute, path: '/recipes', component: RecipesPage });
const buildsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/builds', component: BuildsPage });
const buildDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/builds/$id',
  component: function BuildDetailWrapper() {
    const { id } = buildDetailRoute.useParams();
    return <BuildDetailPage buildId={id} />;
  },
});
const storageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/storage',
  validateSearch: (s: Record<string, unknown>): { bucket?: string; prefix?: string } => ({
    bucket: typeof s.bucket === 'string' ? s.bucket : undefined,
    prefix: typeof s.prefix === 'string' ? s.prefix : undefined,
  }),
  component: function StorageWrapper() {
    const { bucket, prefix } = storageRoute.useSearch();
    return <StoragePage bucket={bucket} prefix={prefix} />;
  },
});
const conflictsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/conflicts', component: ConflictsPage });
const operationsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/operations', component: OperationsPage });
const sharesRoute = createRoute({ getParentRoute: () => rootRoute, path: '/shares', component: SharesPage });
const shareDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/shares/$id',
  component: function ShareDetailWrapper() {
    const { id } = shareDetailRoute.useParams();
    return <ShareDetailPage shareId={id} />;
  },
});
const kbRoute = createRoute({ getParentRoute: () => rootRoute, path: '/kb', component: KnowledgePage });
const settingsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/settings', component: SettingsPage });
const userDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/users/$id',
  component: function UserDetailWrapper() {
    const { id } = userDetailRoute.useParams();
    return <UserDetailPage userId={id} />;
  },
});
const auditLogsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/audit-logs',
  component: AuditLogsPage,
});
const sessionDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sessions/$id',
  component: function SessionDetailWrapper() {
    const { id } = sessionDetailRoute.useParams();
    return <SessionDetailPage sessionId={id} />;
  },
});

const sessionCacheRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sessions/$id/cache',
  component: function SessionCacheWrapper() {
    const { id } = sessionCacheRoute.useParams();
    return <CacheBrowserPage sessionId={id} />;
  },
});

const sessionIdMapRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sessions/$id/id-map',
  component: function SessionIdMapWrapper() {
    const { id } = sessionIdMapRoute.useParams();
    return <IdMapPage sessionId={id} />;
  },
});

const sessionReviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sessions/$id/review',
  component: function SessionReviewWrapper() {
    const { id } = sessionReviewRoute.useParams();
    return <ReviewQueuePage sessionId={id} />;
  },
});

const sessionQuarantineRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sessions/$id/quarantine',
  component: function SessionQuarantineWrapper() {
    const { id } = sessionQuarantineRoute.useParams();
    return <QuarantinePage sessionId={id} />;
  },
});

const ticketsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/tickets', component: TicketsPage });
const ticketDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/tickets/$key',
  component: function TicketDetailWrapper() {
    const { key } = ticketDetailRoute.useParams();
    return <TicketDetailPage issueKey={key} />;
  },
});
const calendarRoute = createRoute({ getParentRoute: () => rootRoute, path: '/calendar', component: CalendarPage });

const routeTree = rootRoute.addChildren([
  dashboardRoute,
  importsRoute,
  ehrsRoute,
  ehrDetailRoute,
  recipesRoute,
  buildsRoute,
  buildDetailRoute,
  storageRoute,
  conflictsRoute,
  operationsRoute,
  sharesRoute,
  shareDetailRoute,
  kbRoute,
  settingsRoute,
  userDetailRoute,
  auditLogsRoute,
  sessionDetailRoute,
  sessionCacheRoute,
  sessionIdMapRoute,
  sessionReviewRoute,
  sessionQuarantineRoute,
  ticketsRoute,
  ticketDetailRoute,
  calendarRoute,
]);

export const router = createRouter({
  routeTree,
  defaultNotFoundComponent: () => (
    <div className="flex flex-col items-center justify-center gap-3 p-24 text-center">
      <div className="text-5xl font-bold" style={{ color: 'var(--text-primary)' }}>404</div>
      <div className="text-sm" style={{ color: 'var(--text-dimmed)' }}>This page doesn’t exist or has moved.</div>
      <a href="/" className="text-[13px] underline" style={{ color: 'var(--accent-blue)' }}>← Back to dashboard</a>
    </div>
  ),
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
